import { createRoute } from "@tanstack/solid-router";

import { rootRoute } from "../__root";
import { sessionActor } from "@/lib/session-machine";
import { readActiveAccountSync } from "@/lib/accounts";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  cloneSession,
  type components,
  HTTP_FORBIDDEN,
  HTTP_UNAUTHORIZED,
  isErrorModel,
  isStatusError,
  isValidSession,
  queryKeys,
  type Session,
} from "@tendly/shared";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { AppLayoutContent } from "@/features/layout/components/app-layout-content";
import { SESSION_CHECK_TIMEOUT_MS, SESSION_STALE_TIME_MS } from "@/lib/constants";

function AppLayout() {
  const { db } = usePowerSyncContext();

  return <AppLayoutContent db={db()} />;
}

const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

let activeSessionCheck: Promise<void> | null = null;

async function runSessionCheck(
  snapshot: ReturnType<typeof sessionActor.getSnapshot>,
): Promise<void> {
  if (snapshot.matches("priming")) {
    await new Promise<void>((resolve) => {
      const sub = sessionActor.subscribe((s) => {
        if (!s.matches("priming")) {
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  if (readActiveAccountSync()?.kind === "guest") {
    return;
  }

  const currentSnapshot = sessionActor.getSnapshot();
  if (currentSnapshot.context.session || currentSnapshot.context.isVerified) {
    return;
  }

  const sessionResult = await (async () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeoutPromise = new Promise<FetchResult>((_resolve, reject) => {
        timer = setTimeout(() => {
          reject(new Error("Session check timeout"));
        }, SESSION_CHECK_TIMEOUT_MS);
      });

      return await Promise.race([
        api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
        }),
        timeoutPromise,
      ]);
    } catch (err) {
      console.warn("[SessionCheck] Error or timeout verifying session:", err);
      return null;
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  })();

  if (!sessionResult) {
    return;
  }

  const { data, error, response } = sessionResult;

  let sessionPayload: Session | null = null;
  if (isValidSession(data)) {
    sessionPayload = cloneSession(data);
  }

  let errorPayload: components["schemas"]["ErrorModel"] | null = null;
  if (isErrorModel(error)) {
    errorPayload = error;
  }

  sessionActor.send({
    type: "SERVER_REPLY",
    session: sessionPayload,
    error: errorPayload,
  });

  if (error) {
    const status = isStatusError(error) ? error.status : response.status;
    if (status === HTTP_UNAUTHORIZED || status === HTTP_FORBIDDEN) {
      return;
    }
    return;
  }

  queryClient.setQueryData(queryKeys.auth.session(), data);
}

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  validateSearch: (search: Record<string, unknown>): { account?: "switch" } =>
    search.account === "switch" ? { account: "switch" } : {},
  beforeLoad: () => {
    const snapshot = sessionActor.getSnapshot();

    if (!snapshot.matches("priming") && snapshot.context.session) {
      const cached = queryClient.getQueryState(queryKeys.auth.session());
      const isStale =
        !cached?.dataUpdatedAt || Date.now() - cached.dataUpdatedAt > SESSION_STALE_TIME_MS;

      if (isStale) {
        void queryClient.prefetchQuery({
          queryKey: queryKeys.auth.session(),
          queryFn: async () =>
            fetchApi(api.GET("/api/auth/get-session", { params: { query: { jwt: true } } })),
          staleTime: SESSION_STALE_TIME_MS,
        });
      }
      return;
    }

    activeSessionCheck ??= (async () => {
      try {
        await runSessionCheck(snapshot);
      } finally {
        activeSessionCheck = null;
      }
    })();
  },

  component: AppLayout,
});
